import { useApolloClient, useQuery } from '@apollo/client/react';
import axios from 'axios';
import { useOrganizationStore } from '../../../stores/useOrganizationStore';
import {
  GET_ORGANIZATION,
  GET_ORGANIZATION_AGENTS,
} from '../../../lib/graphql/queries/organizations';
import { inviteUser } from '../../../api/remiApi';
import type { TeamMemberInput } from '../../../schemas/teamMember.schema';
import {
  refreshAfterMutation,
  shouldUsePaginatedQuery,
  type UseOrganizationUsersOptions,
} from './rosterBranch';

export { shouldUsePaginatedQuery };
export type { UseOrganizationUsersOptions };

export interface FormattedUser {
  value: string;
  label: string;
  id: string;
  email: string;
  firstName: string;
  lastName: string;
  phone_number: string;
  bio: string;
  photoUrl: string;
  slug: string;
  role: string;
  status: string;
  isActive: boolean;
  hasWebAccount: boolean;
  invitedAt: string | null;
  acceptedAt: string | null;
}

interface GQLUser {
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  phone: string;
  role: string;
  status: string;
  isActive: boolean;
  photoUrl: string;
  bio: string | null;
  slug: string | null;
  invitedAt: string | null;
  acceptedAt: string | null;
}

type GQLUsersData = { users: { users: GQLUser[]; total: number; hasMore: boolean } };
/** The subset of User that GET_ORGANIZATION's nested `users` field selects. */
type GQLRosterUser = Omit<GQLUser, 'bio' | 'slug' | 'invitedAt' | 'acceptedAt'>;
type GQLRosterData = {
  organization: { organizationId: string; users: GQLRosterUser[] } | null;
};

export const useOrganizationUsers = (_organizationIdOrOptions?: string | UseOrganizationUsersOptions) => {
  const { organization } = useOrganizationStore();
  const client = useApolloClient();

  const options: UseOrganizationUsersOptions =
    typeof _organizationIdOrOptions === 'object' ? _organizationIdOrOptions : {};
  const explicitOrgId =
    typeof _organizationIdOrOptions === 'string' ? _organizationIdOrOptions : options.organizationId;
  const orgId = explicitOrgId || organization?.id;
  const pageSize = options.pageSize ?? 200;
  const offset = options.offset ?? 0;
  const search = options.search || undefined;
  const enabled = options.enabled ?? true;
  const sortBy = options.sortBy ?? 'firstName';
  const sortOrder = options.sortOrder ?? 'ASC';

  // Callers fall into two shapes. A paginated table (the team roster) drives its
  // own page/search/sort and needs the `users(...)` query. Everyone else —
  // transfer-line pickers, assignee and owner dropdowns, filter panels — renders
  // the whole roster and filters it client-side, and the paginated query clamps
  // to 500 server-side, which silently hid members of larger orgs.
  //
  // Those callers read GET_ORGANIZATION's nested `users` instead: it resolves
  // through the users_by_org DataLoader with no LIMIT, so every member comes
  // back. Reusing that exact document rather than a new one is deliberate — the
  // org store already fetches it at app start, so this is normally a cache hit
  // with no extra request, and an identical selection set cannot fight the org
  // store over the same normalized Organization.users field.
  const isPaginated = shouldUsePaginatedQuery(options);

  const {
    data: pagedData,
    loading: pagedLoading,
    error: pagedError,
    refetch: refetchPaged,
  } = useQuery<GQLUsersData>(GET_ORGANIZATION_AGENTS, {
    variables: {
      organizationId: orgId ?? '',
      limit: pageSize,
      offset,
      sortBy,
      sortOrder,
      search,
    },
    skip: !orgId || !enabled || !isPaginated,
    // 25+ components consume this hook (filters, modals, dropdowns, tables).
    // cache-and-network fired one network request per mount — switching to
    // cache-first dedupes them all to a single fetch per (org, vars) pair.
    // Mutations that change the user list call refetch() explicitly.
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
  });

  const {
    data: rosterData,
    loading: rosterLoading,
    error: rosterError,
    refetch: refetchRoster,
  } = useQuery<GQLRosterData>(GET_ORGANIZATION, {
    variables: { organizationId: orgId ?? '' },
    skip: !orgId || !enabled || isPaginated,
    fetchPolicy: 'cache-first',
    nextFetchPolicy: 'cache-first',
  });

  const loading = isPaginated ? pagedLoading : rosterLoading;
  const error = isPaginated ? pagedError : rosterError;

  // The two branches are separate cache entries, so refreshing one leaves the
  // other stale — a user invited or deleted from the paginated team-roster table
  // would otherwise linger in (or stay missing from) every picker until reload.
  // Evicting the roster field makes any picker, mounted or not, refetch on its
  // next read; the active branch is then refreshed directly.
  const refetch = () =>
    refreshAfterMutation(client.cache, orgId, isPaginated, refetchPaged, refetchRoster);

  const mapUser = (user: GQLUser): FormattedUser => ({
    value: user.userId,
    label: `${user.firstName} ${user.lastName}`.trim() || user.email,
    id: user.userId,
    email: user.email,
    firstName: user.firstName,
    lastName: user.lastName,
    phone_number: user.phone ?? '',
    bio: user.bio ?? '',
    photoUrl: user.photoUrl ?? '',
    slug: user.slug ?? '',
    role: user.role ?? '',
    status: user.status ?? '',
    isActive: user.isActive ?? false,
    hasWebAccount: false,
    invitedAt: user.invitedAt ?? null,
    acceptedAt: user.acceptedAt ?? null,
  });

  // GET_ORGANIZATION omits these four fields from its nested users. Only the
  // team-roster table and its edit modal read them, and that table drives its own
  // pagination, so it always takes the paginated branch and gets them in full.
  const mapRosterUser = (user: GQLRosterUser): FormattedUser =>
    mapUser({ ...user, bio: null, slug: null, invitedAt: null, acceptedAt: null });

  const users: FormattedUser[] = isPaginated
    ? (pagedData?.users?.users ?? []).map(mapUser)
    : (rosterData?.organization?.users ?? []).map(mapRosterUser);

  // The roster query returns every user, so its own length is the total.
  const total: number = isPaginated ? (pagedData?.users?.total ?? 0) : users.length;

  const addTeamMember = async (organizationId: string, data: TeamMemberInput) => {
    try {
      await inviteUser(data.email, organizationId, {
        firstName: data.firstName,
        lastName: data.lastName,
        phoneNumber: data.phoneNumber,
      });
      // cache-first means stale list otherwise — refetch so the new invitee
      // shows up immediately.
      await refetch();
    } catch (error) {
      if (axios.isAxiosError(error)) {
        const detail = error.response?.data?.detail;
        const message = typeof detail === 'string' ? detail : error.message;
        throw new Error(message);
      }
      throw error;
    }
  };

  return {
    users,
    total,
    loading,
    error: error as Error | null | undefined,
    refetch,
    addTeamMember,
  };
};
